import { lazy, Suspense, useCallback, useMemo, useRef } from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import {
  useActivityPopupPriority,
  useIsActivityQualificationAutoEnabled,
  useIsHomeDisplayReady,
  useRealtimeRebateActivityList,
  REALTIME_REBATE_ENTRY_PATH,
} from "../../../modules/homeDisplay/hooks";
import {
  ActivityType,
  TimingType,
} from "../../../modules/homeDisplay/types/homeDisplay.types";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES, useNavigation } from "../../../utils/navigation";

const RealtimeRebatePopupDialog = lazy(() =>
  import("../components/RealtimeRebatePopup/RealtimeRebatePopupDialog").then(
    m => ({ default: m.RealtimeRebatePopupDialog })
  )
);

const REALTIME_REBATE_POPUP_CONFIG = POPUP_CONFIGS.REALTIME_REBATE;
/** entryPoint.popupTimingTypes 里「进入首页」的取值（admin 弹窗时机，与后端同步口径一致） */
const REALTIME_REBATE_POPUP_TIMING_ENTER_HOME = "ENTER_HOME";

export interface RealtimeRebatePopupResult {
  /** 是否有可展示的活动（登录 + 首页配置就绪 + 桶非空） */
  shouldShow: boolean;
  /** 手动打开弹窗（余额不足时机经 popupTriggerManager 调用；可传入口 activityId） */
  trigger: (activityId?: string) => void;
  /** 指定活动是否在桶 list 中（浮标可见性；不传 id 则看 list 是否非空） */
  isActivityAvailable: (activityId?: string) => boolean;
  /** 指定活动是否有可领（浮标红点，取后端 hasClaimable，缺省 false） */
  hasClaimable: (activityId?: string) => boolean;
  /** 指定活动的规则标题（浮标 / 活动列表标题用） */
  getLandingPageTitle: (activityId?: string) => string | undefined;
}

/**
 * 实时反水活动弹窗 hook（结构照 usePhoneVerifyActivityPopup）
 * - 自动弹：admin 弹窗时机勾了「进入首页」（entryPoint.popupTimingTypes 含 ENTER_HOME，后端映射为
 *   ACTIVITY_QUALIFICATION 时机）且弹窗排序启用开关未关；priority 取弹窗排序配置，每人每天最多一次
 * - 余额不足时机：useBalanceInsufficientPopup 按 popupId 经 popupTriggerManager 手动触发
 * - 弹窗按钮「去领取」= 关闭 + 跳活动中心实时反水 tab；首页 banner / 浮标点击直接跳转，不经弹窗
 * - 桶缺失（游客 / 无资格 / 后端未上线）时不弹、浮标不显示
 */
export const useRealtimeRebatePopup = (): RealtimeRebatePopupResult => {
  const { data: userInfo, isAuthenticated } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const list = useRealtimeRebateActivityList();
  const { navigate } = useNavigation();

  // 入口携带的 activityId（ref 同步写、renderContent 触发时读）
  const manualActivityIdRef = useRef<string | null>(null);

  const priority = useActivityPopupPriority(
    ActivityType.REALTIME_REBATE,
    REALTIME_REBATE_POPUP_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );
  const isAutoEnabled = useIsActivityQualificationAutoEnabled(
    ActivityType.REALTIME_REBATE
  );

  const shouldShow = isConfigReady && isAuthenticated && list.length > 0;
  // 自动弹的目标 = renderContent 无入口 id 时展示的 list[0]。
  // useIsActivityQualificationAutoEnabled 找不到配置项时默认 true，不能单靠它：必须该 VO 的 entryPoint
  // 勾了「进入首页」（后端映射为 ACTIVITY_QUALIFICATION 时机下发）才自动弹；entryPoint 缺失一律不弹
  const autoTarget = list[0];
  const autoTrigger =
    isAutoEnabled &&
    autoTarget?.entryPoint?.popupTimingTypes?.includes(
      REALTIME_REBATE_POPUP_TIMING_ENTER_HOME
    ) === true;

  const { trigger: originalTrigger } = useQueuedPopup({
    popupId: REALTIME_REBATE_POPUP_CONFIG.id,
    priority,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
    shouldShow: () => isConfigReady && isAuthenticated && list.length > 0,
    renderContent: onComplete => {
      // 入口携带 id 优先，找不到回落 list[0]
      const manualId = manualActivityIdRef.current;
      const resolved =
        (manualId && list.find(item => item?.id === manualId)) || list[0];
      if (!resolved) return null;
      const close = () => {
        manualActivityIdRef.current = null;
        onComplete();
      };
      return (
        <Suspense fallback={null}>
          <RealtimeRebatePopupDialog
            visible
            activity={resolved}
            onClose={close}
            onGo={() => {
              close();
              navigate(REALTIME_REBATE_ENTRY_PATH);
            }}
          />
        </Suspense>
      );
    },
    // 自动弹每人每天最多一次；手动触发（余额不足时机）不受限
    showOnce: true,
    showOnceGranularity: "day",
    allowManualTrigger: true,
    autoTrigger,
    // 只在首页弹：活动中心本身就是落地页，在那里弹没有意义
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/"],
    dependencies: [list, isAuthenticated, isConfigReady, autoTrigger],
    userBinding: true,
    getUserId: () => userInfo?.userId,
  });

  const trigger = useCallback(
    (activityId?: string) => {
      manualActivityIdRef.current = activityId || null;
      originalTrigger();
    },
    [originalTrigger]
  );

  const findItem = useCallback(
    (activityId?: string) =>
      activityId ? list.find(item => item?.id === activityId) : list[0],
    [list]
  );

  const isActivityAvailable = useCallback(
    (activityId?: string) => !!findItem(activityId),
    [findItem]
  );

  const hasClaimable = useCallback(
    (activityId?: string) => findItem(activityId)?.hasClaimable === true,
    [findItem]
  );

  const getLandingPageTitle = useCallback(
    (activityId?: string) =>
      findItem(activityId)?.entryPoint?.landingPageTitle || undefined,
    [findItem]
  );

  return useMemo(
    () => ({
      shouldShow,
      trigger,
      isActivityAvailable,
      hasClaimable,
      getLandingPageTitle,
    }),
    [
      shouldShow,
      trigger,
      isActivityAvailable,
      hasClaimable,
      getLandingPageTitle,
    ]
  );
};
